import { useState, useEffect } from 'react';
import { Button, Spinner } from '@heroui/react';
import { Download, Eye, Image, X } from '@phosphor-icons/react';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { useSession } from '@clerk/clerk-react';
import { useKeyboardShortcut } from 'shared/hooks/useKeyboardShortcut';

export interface EmailAttachment {
  id?: string;
  name: string;
  size: number;
  contentType: string;
  sharePointFileId?: string;
  sharePointFileUrl?: string;
}

export interface AttachmentEmail {
  id: number;
  messageId: string;
  subject: string;
  body: string;
  from: string;
  receivedDateTime: string;
  direction?: 'sent' | 'received';
  hasAttachments?: boolean;
  attachments?: EmailAttachment[];
}

/**
 * Uit welk postvak/module de bijlage komt. Bepaalt het endpoint waarmee de inhoud
 * opgehaald wordt — daarvóór stond dat hardgecodeerd op helpdesk/verzekeringen,
 * waardoor andere modules dit component niet konden gebruiken.
 */
export type AttachmentSource = 'helpdesk' | 'insurance' | 'reno';

const ENDPOINT_PER_SOURCE: Record<AttachmentSource, string> = {
  helpdesk: 'helpdesk-attachment',
  insurance: 'insurance-attachment',
  reno: 'reno-attachment',
};

/**
 * Generiek over het bijlage- en e-mailtype, zodat een module haar eigen (rijkere) types
 * mag doorgeven zonder cast — bv. de helpdesk, waar `attachment.id` verplicht is en de
 * e-mail extra velden heeft.
 */
interface AttachmentViewerProps<
  A extends EmailAttachment = EmailAttachment,
  E extends AttachmentEmail = AttachmentEmail,
> {
  attachment: A;
  email: E;
  source?: AttachmentSource;
  /**
   * Eigen download-implementatie. Zonder deze prop downloadt het component zelf via
   * het endpoint van `source`.
   */
  onDownload?: (attachment: A, email: E) => Promise<void>;
}

/**
 * Eén bijlage van een e-mail: foto's worden inline getoond (met lightbox), PDF's en
 * andere bestanden openen in een nieuw tabblad of gaan naar de download.
 *
 * Stond eerder in `screens/helpdesk/helpdeskTicketDetail/components/`; hier gedeeld,
 * zodat elke module met e-mailverloop dezelfde weergave krijgt.
 */
export function AttachmentViewer<
  A extends EmailAttachment = EmailAttachment,
  E extends AttachmentEmail = AttachmentEmail,
>({ attachment, email, source = 'helpdesk', onDownload }: AttachmentViewerProps<A, E>) {
  const [showFullImage, setShowFullImage] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  /** Foutboodschap van de laatste poging; de backend legt de reden uit. */
  const [error, setError] = useState<string | null>(null);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const { session } = useSession();

  const getFileExtension = (filename?: string) => {
    if (!filename) return '';
    const parts = filename.toLowerCase().split('.');
    return parts.length > 1 ? (parts.pop() as string) : '';
  };

  const isImageExtension = (ext: string) => /^(png|jpg|jpeg|gif|webp|svg|bmp|tiff?)$/i.test(ext);
  const isImage =
    Boolean(attachment.contentType?.startsWith('image/')) ||
    isImageExtension(getFileExtension(attachment.name));
  const isPdf =
    attachment.contentType === 'application/pdf' ||
    attachment.name.toLowerCase().endsWith('.pdf');

  /**
   * Een doorgestuurde mail als bijlage: Outlook geeft die geen extensie mee (de naam is
   * het onderwerp) en Graph meldt ze als octet-stream. De backend levert ze als `.eml`,
   * dus zeg dat ook — anders lijkt het een bestand dat "niet opengaat".
   */
  const isForwardedMail =
    !/\.[a-z0-9]{2,5}$/i.test(attachment.name) &&
    (attachment.contentType === 'application/octet-stream' ||
      attachment.contentType === 'message/rfc822' ||
      !attachment.contentType);

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'onbekende grootte';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /** De bijlage-inhoud als blob, via het endpoint van deze module. */
  const fetchBlob = async (): Promise<Blob> => {
    const id = attachment.sharePointFileId || attachment.id || attachment.name;
    const response = await fetchWithAuth(
      `${API_URL}/email/${ENDPOINT_PER_SOURCE[source]}/${email.messageId}/${id}`,
      { method: 'GET' },
      session,
    );
    if (!response.ok) {
      // De backend legt uit waaróm het niet kan (bv. een bijlage die nooit bewaard is).
      // Die boodschap tonen is nuttiger dan "probeer het opnieuw".
      const uitleg = await response
        .json()
        .then((data) => data?.message as string | undefined)
        .catch(() => undefined);
      throw new Error(uitleg || `Bijlage ophalen mislukt (${response.status})`);
    }
    return response.blob();
  };

  // Foto's meteen inline laden, zoals in het helpdesk-verloop.
  useEffect(() => {
    if (isImage && !hasStartedLoading && !imageUrl && !error) {
      setHasStartedLoading(true);
      setIsLoading(true);
      fetchBlob()
        .then((blob) => {
          setImageUrl(URL.createObjectURL(blob));
          setError(null);
        })
        .catch((err: Error) => setError(err.message))
        .finally(() => setIsLoading(false));
    }
    // fetchBlob hangt af van attachment/email/session en wordt elke render opnieuw
    // gemaakt; `hasStartedLoading` bewaakt al dat er maar één keer geladen wordt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImage, hasStartedLoading, imageUrl, error]);

  const retryLoad = () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    if (imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    fetchBlob()
      .then((blob) => {
        setImageUrl(URL.createObjectURL(blob));
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    return () => {
      if (imageUrl?.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  // Body-scroll vastzetten zolang de lightbox open staat.
  useEffect(() => {
    if (showFullImage) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showFullImage]);

  useKeyboardShortcut(
    'Escape',
    () => {
      if (showFullImage) setShowFullImage(false);
    },
    [showFullImage],
    'Sluit afbeelding lightbox',
    'global',
  );

  const download = async () => {
    if (onDownload) return onDownload(attachment, email);
    try {
      const blob = await fetchBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Bekijken: een SharePoint-URL kan direct open, anders halen we de inhoud op en
   * openen we die als blob. Dat laatste is nodig voor postvakken zonder SharePoint-kopie
   * (reno) — het endpoint zelf openen zou zonder sessietoken een 401 geven.
   */
  const openInNewTab = async () => {
    if (attachment.sharePointFileUrl && !attachment.sharePointFileUrl.startsWith('#')) {
      window.open(attachment.sharePointFileUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setIsOpening(true);
    try {
      const url = imageUrl ?? URL.createObjectURL(await fetchBlob());
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsOpening(false);
    }
  };

  const isViewable = isImage || isPdf;

  return (
    <div className="border border-neutral-primary/20 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 min-w-0 mr-2">
          <div className="font-medium text-neutral-primary text-sm truncate">{attachment.name}</div>
          <div className="text-xs text-neutral-secondary-mid">
            {formatFileSize(attachment.size)}
            {attachment.contentType ? ` • ${attachment.contentType}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isViewable && (
            <Button
              size="sm"
              onPress={openInNewTab}
              isLoading={isOpening}
              className="bg-brick-primary text-white hover:bg-brick-primary/90 text-xs px-2"
              startContent={<Eye size={14} />}
            >
              {isPdf ? 'PDF' : 'Bekijk'}
            </Button>
          )}

          {isImage && (
            <Button
              size="sm"
              onPress={() => {
                if (imageUrl) setShowFullImage(true);
                else if (!isLoading) retryLoad();
              }}
              disabled={isLoading}
              variant="flat"
              className="bg-brick-secondary-fine text-neutral-primary hover:bg-brick-secondary-fine/80 text-xs px-2"
              startContent={isLoading ? <Spinner size="sm" /> : <Image size={14} />}
            >
              {isLoading ? 'Laden...' : imageUrl ? 'Volledig' : error ? 'Opnieuw' : 'Laden...'}
            </Button>
          )}

          <Button
            size="sm"
            onPress={download}
            className="bg-moss-primary text-white hover:bg-moss-primary/90 text-xs px-2"
            startContent={<Download size={14} />}
          >
            Download
          </Button>
        </div>
      </div>

      {isImage && (
        <div className="mt-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-4 bg-neutral-primary/5 rounded-sm border">
              <Spinner size="sm" color="primary" />
              <span className="ml-2 text-neutral-secondary-mid text-xs">Afbeelding laden...</span>
            </div>
          ) : error ? (
            <div className="p-2 bg-red-50 rounded-sm border border-red-200">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <X size={14} />
                <span className="font-medium text-xs">Bijlage kon niet geladen worden</span>
              </div>
              {/* De backend zegt waarom (bv. een bijlage die nooit bewaard is); een
                  algemene "probeer opnieuw" zou daar niets aan veranderen. */}
              <p className="text-xs text-red-500">{error}</p>
            </div>
          ) : imageUrl ? (
            <div className="relative">
              <div className="bg-neutral-primary/5 rounded-sm border p-1">
                <img
                  src={imageUrl}
                  alt={attachment.name}
                  className="max-w-full h-auto max-h-64 rounded-sm cursor-pointer hover:opacity-90 transition-opacity mx-auto block"
                  onClick={() => setShowFullImage(true)}
                />
                <div className="text-center mt-1">
                  <span className="text-xs text-neutral-secondary-mid">
                    Klik op de afbeelding om volledig scherm te bekijken
                  </span>
                </div>
              </div>

              {showFullImage && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-999 p-4"
                  onClick={() => setShowFullImage(false)}
                >
                  <img
                    src={imageUrl}
                    alt={attachment.name}
                    className="max-w-full max-h-full w-auto h-auto object-contain rounded-sm"
                    onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 2rem)' }}
                  />
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button
                      onClick={download}
                      className="p-2 bg-white/20 text-white hover:bg-white/30 rounded-lg transition-colors"
                      title="Download afbeelding"
                    >
                      <Download size={20} />
                    </button>
                    <button
                      onClick={() => setShowFullImage(false)}
                      className="p-2 bg-white/20 text-white hover:bg-white/30 rounded-lg transition-colors"
                      title="Sluiten"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-sm text-sm">
                    {attachment.name}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {!isImage && (
        <div className="mt-2 p-2 bg-neutral-primary/5 rounded-sm border border-neutral-primary/20 text-neutral-primary text-xs">
          {isPdf
            ? 'PDF-bestand — "PDF" opent het in een nieuw tabblad, "Download" bewaart het.'
            : isForwardedMail
              ? 'Doorgestuurde e-mail — "Download" bewaart ze als .eml-bestand, dat je in Outlook kan openen.'
              : 'Klik op "Download" om dit bestand te bewaren.'}
        </div>
      )}
    </div>
  );
}
